import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Avatar,
  Divider,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import RefreshIcon from '@material-ui/icons/Refresh';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import HistoryIcon from '@material-ui/icons/History';
import SearchIcon from '@material-ui/icons/Search';
import { AuthContext } from '../../contexts/AuthContext';
import { verificationService } from '../../services/verificationService';

import PageHeader from '../../components/ui/PageHeader';
import StatusChip from '../../components/ui/StatusChip';
import EmptyState from '../../components/ui/EmptyState';

const useStyles = makeStyles((theme) => ({
  listPanel: {
    height: 'calc(100vh - 280px)',
    minHeight: 400,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  detailPanel: {
    height: 'calc(100vh - 280px)',
    minHeight: 400,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  verificationCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    border: '1.5px solid',
    borderColor: 'rgba(17,24,39,0.07)',
    cursor: 'pointer',
    transition: 'all 180ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      background: 'rgba(99,102,241,0.03)',
    },
  },
  verificationCardActive: {
    borderColor: theme.palette.primary.main,
    background: 'rgba(99,102,241,0.06)',
  },
  avatar: {
    width: 38,
    height: 38,
    background: 'linear-gradient(135deg, #6366f1, #ec4899)',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 700,
  },
  docImageBox: {
    background: '#111827',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
    position: 'relative',
  },
  docImg: {
    maxHeight: 280,
    maxWidth: '100%',
    transition: 'transform 300ms ease',
    display: 'block',
  },
  imgControls: {
    display: 'flex',
    gap: 4,
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { border: 'none' },
  },
  decisionBar: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(236,72,153,0.05))',
    border: '1px solid rgba(99,102,241,0.12)',
    borderRadius: 10,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  historyEntry: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    '&:last-child': { border: 'none' },
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchInput: {
    flex: 1,
    minWidth: 180,
  },
}));

const initialsOf = (str) => {
  const s = (str || '').trim();
  if (!s) return '?';
  const parts = s.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};

const safeDate = (val) => {
  if (!val) return null;
  try {
    return val.toDate ? val.toDate() : new Date(val);
  } catch {
    return null;
  }
};

const fmt = (val) => {
  const d = safeDate(val);
  if (!d) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (val) => {
  const d = safeDate(val);
  if (!d) return '—';
  return `${fmt(val)} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const STATUS_MAP = {
  pending: { label: 'En attente', status: 'pending' },
  approved: { label: 'Approuvé', status: 'verified' },
  rejected: { label: 'Rejeté', status: 'rejected' },
};

const IdentityVerificationPage = () => {
  const classes = useStyles();
  const { currentUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [verifications, setVerifications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [decision, setDecision] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchVerifications = useCallback(
    async (status = statusFilter) => {
      setLoading(true);
      try {
        const list = await verificationService.getVerifications(status);
        setVerifications(list);
        return list;
      } catch (err) {
        console.error('Error fetching verifications:', err);
        setSnackbar({ open: true, message: 'Erreur lors du chargement des vérifications', severity: 'error' });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    fetchVerifications(statusFilter).then((list) => {
      applySearch(list, searchQuery);
    });
  }, [statusFilter]);

  const applySearch = (list, query) => {
    const q = (query || '').toLowerCase();
    if (!q) {
      setFiltered(list);
      return;
    }
    setFiltered(
      list.filter(
        (v) =>
          (v.user?.displayName || '').toLowerCase().includes(q) ||
          (v.user?.email || '').toLowerCase().includes(q) ||
          (v.user?.id || '').toLowerCase().includes(q)
      )
    );
  };

  useEffect(() => {
    applySearch(verifications, searchQuery);
  }, [searchQuery, verifications]);

  const handleSelect = (v) => {
    setSelected(v);
    setDecision(null);
    setRejectReason('');
    setImageZoom(1);
    setImageRotation(0);
  };

  const handleDecision = (d) => {
    setDecision(d);
    if (d === 'approve') setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      if (decision === 'approve') {
        await verificationService.approveVerification(selected.id, currentUser);
        setSnackbar({ open: true, message: 'Vérification approuvée avec succès', severity: 'success' });
      } else {
        await verificationService.rejectVerification(selected.id, rejectReason, currentUser);
        setSnackbar({ open: true, message: 'Vérification rejetée', severity: 'info' });
      }
      const list = await fetchVerifications(statusFilter);
      applySearch(list, searchQuery);
      setSelected(null);
    } catch (err) {
      console.error('Error processing verification:', err);
      setSnackbar({ open: true, message: 'Erreur lors du traitement', severity: 'error' });
    } finally {
      setProcessing(false);
      setConfirmOpen(false);
      setDecision(null);
    }
  };

  const handleZoom = (dir) => {
    setImageZoom((z) => Math.max(0.4, Math.min(3, z + (dir === 'in' ? 0.2 : -0.2))));
  };
  const handleRotate = (dir) => {
    setImageRotation((r) => r + (dir === 'left' ? -90 : 90));
  };

  const statusCfg = STATUS_MAP[selected?.status] || STATUS_MAP.pending;

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Vérification d\'identité' }]}
        title="Vérification d'identité"
        subtitle={`${filtered.length} demande${filtered.length !== 1 ? 's' : ''}`}
        actions={
          <Tooltip title="Rafraîchir">
            <IconButton
              onClick={() => fetchVerifications(statusFilter)}
              style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <Paper style={{ marginBottom: 16 }}>
        <Box className={classes.filterBar}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="approved">Approuvées</MenuItem>
              <MenuItem value="rejected">Rejetées</MenuItem>
              <MenuItem value="all">Toutes</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            size="small"
            className={classes.searchInput}
            placeholder="Rechercher par nom ou email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ color: '#94a3b8', marginRight: 6 }} />,
            }}
          />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* List panel */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.listPanel} style={{ padding: 16 }}>
            <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 12 }}>
              {filtered.length} demande{filtered.length !== 1 ? 's' : ''}
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress size={32} />
              </Box>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<VerifiedUserIcon style={{ fontSize: 32 }} />}
                title="Aucune demande"
                description={
                  searchQuery
                    ? 'Aucune demande ne correspond à votre recherche.'
                    : 'Aucune demande de vérification pour ce statut.'
                }
              />
            ) : (
              filtered.map((v) => {
                const cfg = STATUS_MAP[v.status] || STATUS_MAP.pending;
                return (
                  <Box
                    key={v.id}
                    className={`${classes.verificationCard} ${
                      selected?.id === v.id ? classes.verificationCardActive : ''
                    }`}
                    onClick={() => handleSelect(v)}
                  >
                    <Box display="flex" alignItems="center" gridGap={10}>
                      <Avatar className={classes.avatar}>
                        {initialsOf(v.user?.displayName || v.user?.email)}
                      </Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {v.user?.displayName || '—'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {v.user?.email}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            {fmt(v.submittedAt)}
                          </Typography>
                          <StatusChip status={cfg.status} label={cfg.label} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>

        {/* Detail panel */}
        <Grid item xs={12} md={8}>
          <Paper className={classes.detailPanel} style={{ padding: selected ? 20 : 0 }}>
            {!selected ? (
              <EmptyState
                icon={<VerifiedUserIcon style={{ fontSize: 36 }} />}
                title="Sélectionner une demande"
                description="Cliquez sur une demande dans la liste pour afficher les détails du document et prendre une décision."
              />
            ) : (
              <Box>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gridGap={12}>
                    <Avatar
                      className={classes.avatar}
                      style={{ width: 44, height: 44, fontSize: '1rem' }}
                    >
                      {initialsOf(selected.user?.displayName || selected.user?.email)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {selected.user?.displayName || '—'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selected.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                  <StatusChip status={statusCfg.status} label={statusCfg.label} />
                </Box>

                <Divider style={{ marginBottom: 16 }} />

                {/* Document type badge */}
                {selected.documentType && (
                  <Box mb={2}>
                    <Chip
                      size="small"
                      label={selected.documentType}
                      style={{ background: 'rgba(99,102,241,0.1)', color: '#4338ca', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </Box>
                )}

                {/* Document image */}
                {selected.documentImageUrl && (
                  <Box mb={2}>
                    <Box className={classes.imgControls}>
                      <Tooltip title="Zoom +">
                        <IconButton size="small" onClick={() => handleZoom('in')} style={{ color: '#e2e8f0' }}>
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zoom -">
                        <IconButton size="small" onClick={() => handleZoom('out')} style={{ color: '#e2e8f0' }}>
                          <ZoomOutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rotation gauche">
                        <IconButton size="small" onClick={() => handleRotate('left')} style={{ color: '#e2e8f0' }}>
                          <RotateLeftIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rotation droite">
                        <IconButton size="small" onClick={() => handleRotate('right')} style={{ color: '#e2e8f0' }}>
                          <RotateRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box className={classes.docImageBox}>
                      <img
                        src={selected.documentImageUrl}
                        alt="Document"
                        className={classes.docImg}
                        style={{
                          transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Info grid */}
                <Grid container spacing={2} style={{ marginBottom: 8 }}>
                  {selected.documentData && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                        Document soumis
                      </Typography>
                      <Paper variant="outlined" style={{ padding: 12, borderRadius: 8 }}>
                        {[
                          ['Nom', selected.documentData.lastName],
                          ['Prénom', selected.documentData.firstName],
                          ['Naissance', selected.documentData.birthDate],
                          ['Nationalité', selected.documentData.nationality],
                          ['N° document', selected.documentData.documentNumber],
                          ['Expiration', selected.documentData.expiryDate],
                        ].map(([label, val]) => (
                          <Box key={label} className={classes.infoRow}>
                            <Typography variant="caption" color="textSecondary">
                              {label}
                            </Typography>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              {val || '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={selected.documentData ? 6 : 12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                      Profil utilisateur
                    </Typography>
                    <Paper variant="outlined" style={{ padding: 12, borderRadius: 8 }}>
                      {[
                        ['Nom', selected.user?.lastName],
                        ['Prénom', selected.user?.firstName],
                        ['Naissance', selected.user?.birthDate],
                        ['Email', selected.user?.email],
                        ['Téléphone', selected.user?.phone],
                        ['ID utilisateur', selected.user?.id?.slice(0, 12) + '…'],
                      ].map(([label, val]) => (
                        <Box key={label} className={classes.infoRow}>
                          <Typography variant="caption" color="textSecondary">
                            {label}
                          </Typography>
                          <Typography variant="caption" style={{ fontWeight: 600 }}>
                            {val || '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>

                {/* Decision (only for pending) */}
                {selected.status === 'pending' && (
                  <Box className={classes.decisionBar}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>
                      Décision
                    </Typography>
                    <Box display="flex" gridGap={12} flexWrap="wrap" mb={decision === 'reject' ? 2 : 0}>
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        style={{ background: '#10b981', color: '#fff' }}
                        onClick={() => handleDecision('approve')}
                        disabled={processing}
                      >
                        Approuver
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelOutlinedIcon />}
                        style={{ borderColor: '#ef4444', color: '#ef4444' }}
                        onClick={() => handleDecision('reject')}
                        disabled={processing}
                      >
                        Rejeter
                      </Button>
                    </Box>

                    {decision === 'reject' && (
                      <Box mt={1.5}>
                        <TextField
                          label="Motif du rejet (obligatoire)"
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={2}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          size="small"
                        />
                        <Box display="flex" justifyContent="flex-end" mt={1.5} gridGap={8}>
                          <Button onClick={() => setDecision(null)} size="small">
                            Annuler
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            style={{ background: '#ef4444', color: '#fff' }}
                            disabled={!rejectReason.trim() || processing}
                            onClick={() => setConfirmOpen(true)}
                          >
                            {processing ? <CircularProgress size={16} color="inherit" /> : 'Confirmer le rejet'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Processed info */}
                {selected.status !== 'pending' && (
                  <Box className={classes.decisionBar}>
                    <Box display="flex" alignItems="center" gridGap={8} mb={1}>
                      <StatusChip status={statusCfg.status} label={statusCfg.label} />
                      <Typography variant="caption" color="textSecondary">
                        {fmtTime(selected.processedAt)}
                        {selected.processedBy ? ` par ${selected.processedBy}` : ''}
                      </Typography>
                    </Box>
                    {selected.rejectReason && (
                      <Typography variant="body2" color="error">
                        Motif: {selected.rejectReason}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* History */}
                {selected.history?.length > 0 && (
                  <Box mt={2}>
                    <Box display="flex" alignItems="center" gridGap={6} mb={1}>
                      <HistoryIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Historique
                      </Typography>
                    </Box>
                    <Paper variant="outlined" style={{ padding: 12, borderRadius: 8 }}>
                      <Box className={classes.historyEntry}>
                        <PersonOutlineIcon fontSize="small" color="action" style={{ marginTop: 2 }} />
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 600 }}>
                            Soumis
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {fmtTime(selected.submittedAt)}
                          </Typography>
                        </Box>
                      </Box>
                      {selected.history.map((e, i) => (
                        <Box key={i} className={classes.historyEntry}>
                          <VerifiedUserIcon fontSize="small" color="action" style={{ marginTop: 2 }} />
                          <Box>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                              {e.action}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {fmtTime(e.date)}{e.by ? ` par ${e.by}` : ''}
                            </Typography>
                            {e.note && (
                              <Typography variant="caption" color="error" display="block">
                                {e.note}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {decision === 'approve' ? "Confirmer l'approbation" : 'Confirmer le rejet'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {decision === 'approve'
              ? "Cette action validera le compte de l'utilisateur. Êtes-vous sûr ?"
              : `Cette action rejettera la demande. L'utilisateur devra soumettre une nouvelle demande.`}
          </DialogContentText>
          {decision === 'reject' && rejectReason && (
            <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>
              Motif: {rejectReason}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={processing}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={processing}
            style={
              decision === 'approve'
                ? { background: '#10b981', color: '#fff' }
                : { background: '#ef4444', color: '#fff' }
            }
          >
            {processing ? <CircularProgress size={18} color="inherit" /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IdentityVerificationPage;
